import { useToast } from "@/hooks/use-toast";
import {
  categoryService,
  type CategoryTranslationInput,
  type CreateCategoryData,
  type UpdateCategoryData,
} from "@/lib/api/categories.service";
import { CategoryStatus } from "@/lib/schema";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const CATEGORIES_QUERY_KEY = ["/content-category"];

interface UseCategoriesOptions {
  search?: string;
  status?: CategoryStatus;
}

export function useCategories(options: UseCategoriesOptions = {}) {
  return useQuery({
    queryKey: [...CATEGORIES_QUERY_KEY, options],
    queryFn: () => categoryService.findAll(options),
    retry: false,
  });
}

export function useCategory(id: string | null) {
  return useQuery({
    queryKey: [...CATEGORIES_QUERY_KEY, id],
    queryFn: () => categoryService.findOne(id!),
    enabled: !!id,
    retry: false,
  });
}

export function useCreateCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (data: CreateCategoryData) => categoryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CATEGORIES_QUERY_KEY,
      });
      toast({ title: "Category created" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to create category",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCategoryData }) =>
      categoryService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CATEGORIES_QUERY_KEY,
      });
      toast({ title: "Category updated" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to update category",
        variant: "destructive",
      });
    },
  });
}

export function useArchiveCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => categoryService.archive(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CATEGORIES_QUERY_KEY,
      });
      toast({ title: "Category archived" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to archive category",
        variant: "destructive",
      });
    },
  });
}

export function useRestoreCategory() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: (id: string) => categoryService.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: CATEGORIES_QUERY_KEY,
      });
      toast({ title: "Category restored" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to restore category",
        variant: "destructive",
      });
    },
  });
}

export function useCategoryTranslations(id: string | null) {
  return useQuery({
    queryKey: [...CATEGORIES_QUERY_KEY, id, "translations"],
    queryFn: () => categoryService.getTranslations(id!),
    enabled: !!id,
    retry: false,
  });
}

export function useAddCategoryTranslation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: CategoryTranslationInput;
    }) => categoryService.addTranslation(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({
        queryKey: [...CATEGORIES_QUERY_KEY, id],
      });
      queryClient.invalidateQueries({
        queryKey: [...CATEGORIES_QUERY_KEY, id, "translations"],
      });
      toast({ title: "Translation added" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to add translation",
        variant: "destructive",
      });
    },
  });
}

export function useUpdateCategoryTranslation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      categoryId,
      translationId,
      data,
    }: {
      categoryId: string;
      translationId: string;
      data: Partial<CategoryTranslationInput>;
    }) => categoryService.updateTranslation(translationId, data),
    onSuccess: (_, { categoryId }) => {
      queryClient.invalidateQueries({
        queryKey: [...CATEGORIES_QUERY_KEY, categoryId],
      });
      queryClient.invalidateQueries({
        queryKey: [...CATEGORIES_QUERY_KEY, categoryId, "translations"],
      });
      toast({ title: "Translation updated" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to update translation",
        variant: "destructive",
      });
    },
  });
}

export function useDeleteCategoryTranslation() {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: ({
      categoryId,
      translationId,
    }: {
      categoryId: string;
      translationId: string;
    }) => categoryService.deleteTranslation(translationId),
    onSuccess: (_, { categoryId }) => {
      queryClient.invalidateQueries({
        queryKey: [...CATEGORIES_QUERY_KEY, categoryId],
      });
      queryClient.invalidateQueries({
        queryKey: [...CATEGORIES_QUERY_KEY, categoryId, "translations"],
      });
      toast({ title: "Translation deleted" });
    },
    onError: (err: any) => {
      toast({
        title: err.message || "Failed to delete translation",
        variant: "destructive",
      });
    },
  });
}
